import React, { useState, useRef, useLayoutEffect } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { formatChartAxisDateValue as formatDateValue } from "../../utils/formatChartAxisDate";
import {
  humanizeColumnLabel,
  isPieChartEligible,
} from "../Common/EmcureDataCharts/emcureChartConstants";

/** Tooltip Y values: numbers or locale strings (e.g. "1,34,93,040"). Used by pie + cartesian charts. */
export function parseChartTooltipNumber(v) {
  if (v == null) return NaN;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const cleaned = String(v).replace(/[₹$€,\s]/g, "").trim();
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

/** Round chart metrics to match SQL table display (whole numbers or 2 decimals). */
export function normalizeChartMetricNumber(v) {
  const n = parseChartTooltipNumber(v);
  if (!Number.isFinite(n)) return 0;
  if (Number.isInteger(n) || Math.abs(n - Math.round(n)) < 1e-9) {
    return Math.round(n);
  }
  return parseFloat(n.toFixed(2));
}

const INDIAN_CRORE = 10_000_000;
const INDIAN_LAKH = 100_000;

/** Compact cr/l label for a positive value already scaled by divisor. */
function formatIndianCompactUnit(absValue, divisor, suffix) {
  const scaled = absValue / divisor;
  let decimals = 2;
  if (scaled >= 100) decimals = 0;
  else if (scaled >= 10) decimals = 1;
  const rounded =
    decimals === 0 ? Math.round(scaled) : parseFloat(scaled.toFixed(decimals));
  // Only trim fractional trailing zeros (e.g. 1.50 → 1.5). Do not strip integer
  // trailing digits — String(70).replace(/\.?0+$/, "") incorrectly becomes "7".
  const str = Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
  return `${str} ${suffix}`;
}

/** Locale string for chart axes and legends (cr / l for large Indian values). */
export function formatChartMetricValue(v) {
  const n = normalizeChartMetricNumber(v);
  if (!Number.isFinite(n)) return "0";

  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";

  if (abs >= INDIAN_CRORE) {
    return sign + formatIndianCompactUnit(abs, INDIAN_CRORE, "cr");
  }
  if (abs >= INDIAN_LAKH) {
    return sign + formatIndianCompactUnit(abs, INDIAN_LAKH, "l");
  }

  if (Number.isInteger(n)) {
    return n.toLocaleString("en-IN");
  }
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Full value for chart tooltips (no cr/l abbreviation). */
export function formatChartTooltipValue(v) {
  const n = normalizeChartMetricNumber(v);
  if (!Number.isFinite(n)) return String(v ?? "");
  if (Number.isInteger(n)) {
    return n.toLocaleString("en-IN");
  }
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const PIE_TOOLTIP_BOX_STYLE = {
  fontSize: "13px",
  backgroundColor: "#FFFFFF",
  borderRadius: "10px",
  padding: "12px 16px",
  border: "1px solid #E5E7EB",
  fontWeight: 600,
  boxShadow:
    "0 10px 15px -3px rgba(15, 23, 42, 0.1), 0 4px 6px -4px rgba(15, 23, 42, 0.1)",
};

/**
 * Pie tooltip aligned with bar/line: optional category header, then "Metric : value".
 * Avoids Recharts default showing xValue twice when the slice name is numeric.
 */
export function EmcurePieTooltip({ active, payload, yColumn, formatCategoryLabel }) {
  if (!active || !payload?.length) return null;

  const dataPoint = payload[0]?.payload || {};
  const rawValue = dataPoint.yValue ?? payload[0]?.value;
  const formattedValue = formatChartTooltipValue(rawValue);
  const metricLabel = humanizeColumnLabel(yColumn);

  const formatCat =
    formatCategoryLabel ??
    ((xv) => {
      const formatted = formatDateValue(xv);
      if (formatted !== xv && formatted != null) return String(formatted);
      return String(xv ?? "");
    });

  const categoryLabel = formatCat(dataPoint.xValue ?? dataPoint.name ?? "").trim();
  const categoryLooksNumeric =
    categoryLabel !== "" &&
    formatChartTooltipValue(dataPoint.xValue) === formattedValue;
  const showCategoryHeader = categoryLabel !== "" && !categoryLooksNumeric;

  return (
    <div style={PIE_TOOLTIP_BOX_STYLE}>
      {showCategoryHeader ? (
        <div
          style={{
            color: "#111827",
            fontWeight: 700,
            marginBottom: 6,
            maxWidth: 260,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={categoryLabel}
        >
          {categoryLabel}
        </div>
      ) : null}
      <div style={{ color: "#111827" }}>
        <span style={{ fontWeight: 700 }}>{metricLabel}: </span>
        <span>{formattedValue}</span>
      </div>
    </div>
  );
}

/** Pie in chat bubbles: width follows the message column (ResizeObserver). */
export function ChatBubbleResponsivePie({ pieData, pieColors, gradIdPrefix, yColumn }) {
  const axisLabel = (xv) => {
    const formatted = formatDateValue(xv);
    if (formatted !== xv && formatted !== undefined && formatted !== null) {
      return String(formatted);
    }
    return String(xv ?? "");
  };

  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 280, h: 320 });

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const rw = el.getBoundingClientRect().width;
      const w = Math.max(200, Math.min(720, Math.floor(rw > 0 ? rw : 280)));
      const h = Math.min(440, Math.max(260, Math.round(w * 0.88)));
      setSize((prev) => (prev.w !== w || prev.h !== h ? { w, h } : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const outerRadius = Math.min(150, size.w * 0.3, size.h * 0.36);
  const innerRadius = Math.max(36, Math.round(outerRadius * 0.47));

  return (
    <div ref={wrapRef} className="chat-bubble-pie-chart-root">
      <div className="admin-pie-chart-container chat-bubble-pie-chart-container">
        <div className="admin-pie-chart-visual">
          <PieChart width={size.w} height={size.h}>
            <defs>
              {pieData.map((_, i) => (
                <linearGradient
                  key={`pieGrad-${gradIdPrefix}-${i}`}
                  id={`chatPieGrad-${gradIdPrefix}-${i}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={pieColors[i % pieColors.length]} stopOpacity={1} />
                  <stop offset="100%" stopColor={pieColors[i % pieColors.length]} stopOpacity={0.7} />
                </linearGradient>
              ))}
            </defs>
            <Tooltip
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{ pointerEvents: "none", outline: "none", zIndex: 50 }}
              content={
                <EmcurePieTooltip
                  yColumn={yColumn}
                  formatCategoryLabel={axisLabel}
                />
              }
              isAnimationActive={false}
            />
            <Pie
              data={pieData}
              dataKey="yValue"
              nameKey="xValue"
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={pieData.length > 20 ? 1 : 3}
              label={({ cx, cy, midAngle, outerRadius: oR, percent, xValue }) => {
                const RADIAN = Math.PI / 180;
                const radius = oR + Math.min(25, size.w * 0.06);
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                const pct = (percent * 100).toFixed(1);
                const name = axisLabel(xValue);
                const shortName = name.length > 10 ? name.substring(0, 8) + "…" : name;
                return (
                  <text
                    x={x}
                    y={y}
                    textAnchor={x > cx ? "start" : "end"}
                    dominantBaseline="central"
                    fontSize={11}
                    fontWeight={500}
                    fill="#374151"
                  >
                    {`${shortName} ${pct}%`}
                  </text>
                );
              }}
              labelLine={{ stroke: "#94A3B8", strokeWidth: 1, strokeDasharray: "3 3" }}
              isAnimationActive={false}
            >
              {pieData.map((_, i) => (
                <Cell key={`cell-${i}`} fill={`url(#chatPieGrad-${gradIdPrefix}-${i})`} />
              ))}
            </Pie>
          </PieChart>
        </div>
        <div className="admin-pie-chart-legend">
          {pieData.map((entry, i) => (
            <div key={i} className="admin-pie-legend-item">
              <span
                className="admin-pie-legend-dot"
                style={{ backgroundColor: pieColors[i % pieColors.length] }}
              />
              <span className="admin-pie-legend-label" title={axisLabel(entry.xValue)}>
                {(() => {
                  const disp = axisLabel(entry.xValue);
                  return disp.length > 20 ? disp.substring(0, 18) + "…" : disp;
                })()}
              </span>
              <span className="admin-pie-legend-value">
                {formatChartMetricValue(entry.yValue)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** API may send "Bar", "LINE", or chart_type; pie only when pointCount is 2–10. */
export function resolveChatChartType(chart, override, pointCount) {
  const pieOk = isPieChartEligible(pointCount);
  const allowed = new Set(["bar", "line", ...(pieOk ? ["pie"] : [])]);

  if (override != null && override !== "") {
    const o = String(override).trim().toLowerCase();
    if (o === "pie" && !pieOk) return "bar";
    if (allowed.has(o)) return o;
  }

  const raw = chart?.type ?? chart?.chart_type;
  const s = raw == null ? "" : String(raw).trim().toLowerCase();
  if (s === "scatter") return "line";
  if (s === "pie" && !pieOk) return "bar";
  if (allowed.has(s)) return s;
  return "bar";
}

export function ChatBubbleResponsiveCartesian({ height = 280, minOuterWidth = 280, innerMinWidth, children }) {
  const wrapRef = useRef(null);
  const [outerW, setOuterW] = useState(minOuterWidth);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const rw = el.getBoundingClientRect().width;
      setOuterW(Math.max(minOuterWidth, Math.floor(rw > 0 ? rw : minOuterWidth)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [minOuterWidth]);

  const chartWidth =
    innerMinWidth != null && innerMinWidth > outerW ? innerMinWidth : outerW;

  return (
    <div
      ref={wrapRef}
      className="chat-bubble-cartesian-chart-root"
      style={{
        width: "100%",
        maxWidth: "100%",
        minWidth: minOuterWidth,
        height,
        position: "relative",
      }}
    >
      {outerW > 0 && children(chartWidth, height)}
    </div>
  );
}
